/**
 * Acciones de REDUX para la autenticación en 2 pasos (MFA).
 *
 * Dos métodos conviven (ver daysi-cloud-functions/mfa/):
 *  - TOTP nativo (Identity Platform): el enrolamiento y el sign-in usan la
 *    API MODULAR de `firebase/auth` — TOTP no existe en compat. La app fue
 *    inicializada en compat, pero comparte registro con la modular, así que
 *    `getAuth(getApp())` opera sobre la misma sesión.
 *  - OTP por email (custom): callables `sendLoginOtp` / `verifyLoginOtp`.
 *    Tras verificar se fuerza `getIdToken(true)` para que el custom claim
 *    `mfaAuthTime` llegue al cliente y las rules dejen pasar las lecturas.
 */

import { getApp } from 'firebase/app';
import { getAuth, multiFactor, TotpMultiFactorGenerator } from 'firebase/auth';
import { getMfaResolver, clearMfaResolver } from '../../lib/mfa_resolver';

// Mismo patrón de callables que application/lib/client_log.js
const callable = (firebase, name) => firebase().app().functions().httpsCallable(name);

export const mfa = {

  /**
   * Envía un código OTP al correo del usuario (sesión del 1er factor activa).
   * @param {'login'|'enroll'} purpose
   * @returns {Promise<{ok: boolean, cooldownSeconds: number, email: string}>}
   */
  sendOtp: (purpose = 'login') => (dispatch, state, firebase) =>
    callable(firebase, 'sendLoginOtp')({ purpose }).then(res => res.data),

  /**
   * Verifica el código OTP. En éxito refresca el ID token para que el
   * clearance (`mfaAuthTime`) surta efecto en las Security Rules.
   * @param {string} code
   * @param {'login'|'enroll'} purpose
   */
  verifyOtp: (code, purpose = 'login') => async (dispatch, state, firebase) => {
    const res = await callable(firebase, 'verifyLoginOtp')({ code, purpose });
    await firebase().auth().currentUser.getIdToken(true);
    return res.data;
  },

  /**
   * Completa el sign-in TOTP con el código de la app autenticadora.
   * Usa el resolver guardado por actions/auth/email.js en el singleton.
   * En éxito dispara onAuthStateChanged → el Router retoma el flujo normal.
   */
  resolveTotpSignIn: (code) => async () => {
    const resolver = getMfaResolver();
    if (!resolver) {
      const error = new Error('La verificación expiró. Inicie sesión de nuevo.');
      error.code = 'auth/totp-challenge-timeout';
      throw error;
    }
    const hint = resolver.hints.find(h => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
    if (!hint) {
      const error = new Error('La cuenta no tiene un autenticador registrado.');
      error.code = 'auth/no-totp-factor';
      throw error;
    }
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code);
    const credential = await resolver.resolveSignIn(assertion);
    clearMfaResolver();
    return credential;
  },

  /**
   * Inicia el enrolamiento TOTP: genera el secreto y la URL para el QR.
   * Requiere email verificado (Firebase lanza auth/unverified-email si no).
   * El `secret` retornado NO es serializable: mantenerlo en estado local
   * del componente y pasarlo tal cual a finishTotpEnrollment.
   */
  startTotpEnrollment: () => async () => {
    const user = getAuth(getApp()).currentUser;
    const session = await multiFactor(user).getSession();
    const secret = await TotpMultiFactorGenerator.generateSecret(session);
    return {
      secret,
      qrUrl: secret.generateQrCodeUrl(user.email || 'usuario', 'DAYSI'),
      secretKey: secret.secretKey,
    };
  },

  /**
   * Confirma el enrolamiento TOTP con el primer código de la app y lo
   * registra server-side (users/{uid}.mfa via callable — el cliente no
   * puede escribir ese campo por rules).
   */
  finishTotpEnrollment: (secret, code) => async (dispatch, state, firebase) => {
    const user = getAuth(getApp()).currentUser;
    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
    await multiFactor(user).enroll(assertion, 'App de autenticación');
    await callable(firebase, 'finalizeTotpEnrollment')({});
    await firebase().auth().currentUser.getIdToken(true);
  },

  /**
   * Recuperación de autenticador perdido (sin sesión). La respuesta es
   * genérica: no revela si la cuenta existe.
   */
  requestReset: (email, password) => (dispatch, state, firebase) =>
    callable(firebase, 'requestMfaReset')({ email, password }).then(res => res.data),

  confirmReset: (email, code) => (dispatch, state, firebase) =>
    callable(firebase, 'confirmMfaReset')({ email, code }).then(res => res.data),

  /**
   * Desactivación voluntaria (la callable la rechaza si el admin la exige).
   */
  disable: () => async (dispatch, state, firebase) => {
    const res = await callable(firebase, 'disableMfa')({});
    await firebase().auth().currentUser.getIdToken(true);
    return res.data;
  },

};
